/**
 * プロンプトが使われる業務ワークフローの定義と逆引きヘルパー。
 *
 * 2026-05-30: BE が `Prompt.workflow_type` フィールドに対応。
 * `findWorkflowByPrompt()` はそちらを優先して使用する。
 * BE が値を返さない旧データや、未登録の workflow_type に対しては
 * title の命名規則による fallback を維持する
 *
 * 2026-09-26: キーを BE の正式な値（daily_report / weekly_report / monthly_report /
 * health_management）にそろえた（課題 #59）。以前の FE は daily_report_comment 等を
 * 送っており、BE は前方一致で正式な値に直して保存していた。一方で API は正式な値を
 * 返すため、FE の定数と一致しなかった（タイトルの命名規則による fallback で
 * 救われていたが、命名どおりでないタイトルでは用途が外れて見え、そのまま保存すると
 * 用途が外れる）。旧い値は normalizeWorkflowType() で正式な値に寄せる
 */

export interface PromptWorkflow {
  /** BE の workflow_type の正式な値（app/models/prompt.py の WORKFLOW_TYPE_VALUES） */
  key: string;
  /** 画面表示用の日本語ラベル */
  label: string;
  /** 用途の説明（一覧ツールチップ・編集画面のガイダンス等で使う） */
  description: string;
  /** この業務で呼ばれるタイミング・担当者の補足 */
  triggeredBy: string;
  /** 新規作成時の推奨タイトル */
  titleSuggestion: string;
  /** 既存プロンプトの title からこのワークフローを判定する正規表現 */
  matchTitle: RegExp;
}

/**
 * 現時点のワークフロー一覧。
 * 新しい用途を追加する場合はここに 1 エントリ足すだけで、一覧・編集画面とも追従する
 */
export const PROMPT_WORKFLOWS: readonly PromptWorkflow[] = [
  {
    key: 'daily_report',
    label: '日次レポート（当日音声振り返り）',
    description: '従業員が当日録音した音声を要約し、コンディション振り返りコメントを生成します。',
    triggeredBy: '従業員が音声記録を送信 → voice-job 完了後に呼び出し',
    titleSuggestion: 'daily_report_comment_v1',
    matchTitle: /^daily_report_comment/i,
  },
  {
    key: 'monthly_report',
    label: '月次振り返りレポート',
    description: '従業員の 1 か月分の日次レポートをまとめて要約します。',
    triggeredBy: '月次バッチ処理 / 従業員の月次振り返り画面表示時',
    titleSuggestion: 'monthly_report_comment_v1',
    matchTitle: /^monthly_report_comment/i,
  },
  {
    // 週次レポート（課題 #59 段階 1）
    key: 'weekly_report',
    label: '週次レポート',
    description: 'その週の記録の傾向をまとめます。MCS が出るまでの期間に、続ける手がかりを返すためのものです。',
    triggeredBy: '週次バッチ処理',
    titleSuggestion: 'weekly_report_comment_v1',
    matchTitle: /^weekly_report/i,
  },
  {
    key: 'health_management',
    label: '健康経営レポート（管理者向け）',
    description: '管理者が発行する健康経営レポートの本文（エグゼクティブサマリ）を生成します。',
    triggeredBy: '管理者が「健康経営レポート生成」を実行',
    titleSuggestion: 'health_management_report_default_v1',
    matchTitle: /^health_management_report/i,
  },
] as const;

/**
 * プロンプト 1 件から該当するワークフローを検索する。未マッチの場合は null。
 *
 * 2026-05-30: BE が workflow_type を返すようになったため、まずはそちらを参照する。
 * 値が無い／PROMPT_WORKFLOWS に未登録の場合は title の命名規則で fallback する
 */
export function findWorkflowByPrompt(prompt: {
  title?: string;
  workflow_type?: string | null;
}): PromptWorkflow | null {
  // BE が workflow_type を返す場合はそれを優先（2026-05-30 〜）
  const byKey = findWorkflowByKey(prompt.workflow_type);
  if (byKey) return byKey;
  // fallback: title の命名規則で逆引き（旧データ / 未登録 workflow_type 対策）
  if (prompt.title) {
    const byTitle = PROMPT_WORKFLOWS.find((w) => w.matchTitle.test(prompt.title!));
    if (byTitle) return byTitle;
  }
  return null;
}

/** key 指定でワークフローを取得する。未定義の場合は null */
export function findWorkflowByKey(key: string | null | undefined): PromptWorkflow | null {
  const canonical = normalizeWorkflowType(key);
  return canonical ? (PROMPT_WORKFLOWS.find((w) => w.key === canonical) ?? null) : null;
}

/**
 * workflow_type を正式な値に寄せる。旧い FE の値（daily_report_comment 等）や
 * 端末に残った下書きの値も、BE の API 層（_validate_workflow_type）と同じく
 * 前方一致で正式な値に直す。当てはまらなければ null。
 */
export function normalizeWorkflowType(value: string | null | undefined): string | null {
  if (!value) return null;
  const keys = PROMPT_WORKFLOWS.map((w) => w.key).sort((a, b) => b.length - a.length);
  return keys.find((k) => value === k) ?? keys.find((k) => value.startsWith(k)) ?? null;
}
